import { DIRECTION } from '../../common/direction.js';
import { exhaustiveGuard } from '../../utils/guard.js';
import { Character } from './character.js';
import { getTargetPositionFromGameObjectPositionAndDirection } from '../../utils/grid-utils.js';
import { TILE_SIZE } from '../../config.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../../utils/data-manager.js';
import { SKIN_IDLE_FRAME_CONFIG, resolveSkinKey } from './skins.js';

// Overworld skin sheets are 32px; scale x2 so the player reads at ~one tile (TILE_SIZE 64).
const SKIN_SCALE = 2;

/**
 * @typedef PlayerConfigProps
 * @type {object}
 * @property {Phaser.Tilemaps.TilemapLayer} collisionLayer
 * @property {Phaser.Tilemaps.ObjectLayer} [entranceLayer]
 * @property {(entranceName: string, entranceId: string, isBuildingEntrance: boolean) => void} enterEntranceCallback
 * @property {string} [skinKey] overworld skin asset key; defaults to the saved/selected skin
 */

/**
 * @typedef {Omit<import('./character').CharacterConfig, 'assetKey' | 'idleFrameConfig'> & PlayerConfigProps} PlayerConfig
 */

export class Player extends Character {
  /** @type {Phaser.Tilemaps.ObjectLayer | undefined} */
  #entranceLayer;
  /** @type {(entranceName: string, entranceId: string, isBuildingEntrance: boolean) => void} */
  #enterEntranceCallback;
  /** @type {string} */
  #skinKey;

  /**
   * @param {PlayerConfig} config
   */
  constructor(config) {
    // Resolve the chosen overworld skin (set in starter-select), falling back to the default.
    const skinKey = resolveSkinKey(
      config.skinKey ?? dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_SKIN)
    );
    super({
      ...config,
      assetKey: skinKey,
      scale: SKIN_SCALE,
      origin: { x: 0, y: 0.2 },
      idleFrameConfig: { ...SKIN_IDLE_FRAME_CONFIG },
    });
    this.#skinKey = skinKey;
    this.#entranceLayer = config.entranceLayer;
    this.#enterEntranceCallback = config.enterEntranceCallback;
  }

  /**
   * @param {import('../../common/direction.js').Direction} direction
   * @param {boolean} [isRunning=false]
   * @returns {void}
   */
  moveCharacter(direction, isRunning = false) {
    super.moveCharacter(direction, isRunning);

    switch (this._direction) {
      case DIRECTION.DOWN:
      case DIRECTION.LEFT:
      case DIRECTION.RIGHT:
      case DIRECTION.UP:
        if (
          !this._phaserGameObject.anims.isPlaying ||
          this._phaserGameObject.anims.currentAnim?.key !== `SKIN_${this.#skinKey}_${this._direction}`
        ) {
          this._phaserGameObject.play(`SKIN_${this.#skinKey}_${this._direction}`);
        }
        this._phaserGameObject.anims.timeScale = this._isRunning ? 2 : 1;
        break;
      case DIRECTION.NONE:
        break;
      default:
        // We should never reach this default case
        exhaustiveGuard(this._direction);
    }

    // validate character is not moving and that the target position belongs to an entrance
    if (!this._isMoving && this.#entranceLayer !== undefined) {
      const targetPosition = getTargetPositionFromGameObjectPositionAndDirection(
        { x: this._phaserGameObject.x, y: this._phaserGameObject.y },
        this._direction
      );
      const nearbyEntrance = this.#entranceLayer.objects.find((object) => {
        if (!object.x || !object.y) {
          return false;
        }
        return object.x === targetPosition.x && object.y - TILE_SIZE === targetPosition.y;
      });

      if (!nearbyEntrance) {
        return;
      }

      // entrance is nearby and the player is trying to enter that location
      const connectsToProperty = nearbyEntrance.properties.find((property) => property.name === 'connects_to');
      const entranceIdProperty = nearbyEntrance.properties.find((property) => property.name === 'entrance_id');
      // skip if the tiled entrance is missing the required properties (would otherwise crash)
      if (connectsToProperty === undefined || entranceIdProperty === undefined) {
        return;
      }
      const entranceName = connectsToProperty.value;
      const entranceId = entranceIdProperty.value;
      const isBuildingEntrance =
        nearbyEntrance.properties.find((property) => property.name === 'is_building')?.value || false;
      this.#enterEntranceCallback(entranceName, entranceId, isBuildingEntrance);
    }
  }
}
